<template>
  <section class="container pb-8">
    <!-- 考試說明頁 -->
    <div v-if="ShowInstructions" class="row">
      <div class="col-12">
        <ExamInstructions
          :PaperInfo="PaperDetail"
          @start-exam="StartExam"
          @cancel-exam="CancelExam"
        />
      </div>
    </div>

    <template v-else>
      <!-- Header -->
      <div class="row">
        <div class="col-12">
          <div class="exam-header bg-green">
            <div class="header-left">
              <h4 class="exam-title">
                考試名稱：{{ PaperDetail.essayPapersName }}
              </h4>
            </div>
            <div class="header-right">
              <template v-if="IsPreviewMode">
                <button @click="ContinueExam" class="btn btn-secondary">
                  <i class="fa fa-chevron-left"></i> 繼續作答
                </button>
                <button
                  @click="ShowFinalConfirmDialog"
                  class="btn btn-success"
                  :disabled="IsSubmitting"
                >
                  <i class="fa fa-check-circle"></i> 結束作答
                </button>
              </template>

              <template v-else>
                <button
                  v-if="false"
                  @click="GoToPreview"
                  class="btn btn-secondary"
                  :disabled="IsSaving || IsSubmitting"
                >
                  <!-- 考選部拔除此功能，先關閉 -->
                  <i class="fa fa-list"></i> 瀏覽作答情形
                </button>
                <button
                  @click="ShowFinalConfirmDialog"
                  class="btn btn-success"
                  :disabled="IsSaving || IsSubmitting"
                >
                  <i class="fa fa-check-circle"></i> 結束作答
                </button>
              </template>
            </div>
          </div>
        </div>
      </div>

      <!-- Status Bar -->
      <div class="row mb-2">
        <div class="col-12">
          <div class="status-bar">
            <div class="status-row">
              <div class="status-item">
                <span class="label">類科：</span>
                <span class="value">{{ PaperDetail.displayCategory }}</span>
              </div>
              <div class="status-item">
                <span class="label">姓名：</span>
                <span class="value">{{ PaperDetail.studentName }}</span>
              </div>
              <div class="status-item">
                <span class="label">科目：</span>
                <span class="value">{{ PaperDetail.subjectName }}</span>
              </div>
            </div>

            <div class="status-divider"></div>

            <div class="status-row">
              <div class="status-item">
                <span class="label">題數：</span>
                <span class="value">{{ Questions.length }} 題</span>
              </div>
              <div class="status-item">
                <span class="label">已作答題數：</span>
                <span class="value">{{ AnsweredCount }} 題</span>
              </div>
              <div class="status-item" :class="{ 'warning': UnansweredCount > 0 }">
                <span class="label">未作答：</span>
                <span class="value">{{ UnansweredCount }} 題</span>
              </div>
              <div class="status-item">
                <i class="fa fa-clock-o status-icon-red"></i>
                <span class="label">剩餘時間：</span>
                <span class="value text-red">{{ FormatTime(RemainingSeconds) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ========== 預覽模式 (Mode B: Review) ========== -->
      <div v-if="IsPreviewMode">
        <!-- Review Table -->
        <div class="row mb-2">
          <div class="col-12">
            <div class="review-table">
              <!-- Table Header -->
              <div class="table-header">
                <div class="col-number">申論題號</div>
                <div class="col-content">作答內容</div>
              </div>

              <!-- Table Body -->
              <div class="table-body">
                <a
                  v-for="(question, index) in Questions"
                  :key="question.essayQuestionsId"
                  class="table-row"
                  :class="{ unanswered: !HasAnswer(question.essayQuestionsId) }"
                  href="javascript:void(0)"
                  @click="GoToQuestionFromPreview(index)"
                >
                  <div class="col-number">
                    <span class="question-link">
                      第 {{ index + 1 }} 題
                    </span>
                    <i
                      v-if="MarkerSelections[question.essayQuestionsId]"
                      class="fa marker-icon"
                      :class="[
                        GetMarkerIcon(MarkerSelections[question.essayQuestionsId]),
                        GetMarkerClass(MarkerSelections[question.essayQuestionsId])
                      ]"
                    ></i>
                  </div>
                  <div class="col-content">
                    <div
                      v-if="HasAnswer(question.essayQuestionsId)"
                      class="answer-preview"
                      v-html="GetAnswerPreview(question.essayQuestionsId)"
                    ></div>
                    <span v-else class="empty-text">（尚未作答）</span>
                  </div>
                </a>
              </div>
            </div>

            <!-- Footer Note -->
            <div class="footer-note">
              <i class="fa fa-info-circle"></i>
              <p>注意事項：1.上方顯示所有題目之題號及作答答案 2.若為<span class="text-red">紅色</span>底色代表尚未作答</p>
            </div>
          </div>
        </div>
      </div>

      <!-- ========== 作答模式 (Mode A: Exam) ========== -->
      <div v-else>
        <!-- Toolbar (Radio Group) -->
        <div class="row mb-2">
          <div class="col-12">
            <div class="exam-toolbar">
              <div class="toolbar-left">
                <button
                  @click="PrevQuestion"
                  class="btn btn-sm btn-secondary"
                  :disabled="IsFirstQuestion || IsSaving"
                >
                  <i class="fa fa-chevron-left"></i> 上一題
                </button>
                <button
                  @click="NextQuestion"
                  class="btn btn-sm btn-secondary"
                  :disabled="IsLastQuestion || IsSaving"
                >
                  下一題 <i class="fa fa-chevron-right"></i>
                </button>
                <button
                  @click="SaveCurrentAnswer"
                  class="btn btn-sm btn-primary"
                  :disabled="IsSaving"
                >
                  <i class="fa fa-save"></i>
                  {{ IsSaving ? '儲存中...' : '儲存' }}
                </button>
                <button
                  @click="TogglePreviewMode"
                  class="btn btn-sm btn-secondary btn-toggle-preview"
                  :class="{ active: IsSingleQuestionPreview }"
                  :disabled="IsSaving"
                >
                  <i class="fa" :class="IsSingleQuestionPreview ? 'fa-eye-slash' : 'fa-eye'"></i>
                  {{ IsSingleQuestionPreview ? '結束預覽' : '預覽' }}
                </button>
                <div class="marker-selector">
                  <el-tooltip placement="bottom">
                  <div slot="content">
                    <p>
                      ==[操作引導]==
                      <br>1. 開始作答時，務必確認已經選擇輸入法（滑鼠游標移到作答區，選擇輸入法）。
                      <br>2. 作答區僅提供一次還原功能。
                      <br>3. 編號及縮排階層輔助功能（預設4階層，「一、」、「(一)」、「1.」、「(1)」）。
                      <br>4. 在編號階層中，想換行且不想進到下一個編號，則可以使用「Shift + Enter」進行換行。
                    </p>
                  </div>
                  <el-button>操作引導</el-button>
                </el-tooltip>
                <el-tooltip placement="bottom">
                  <div slot="content">
                    <p>
                      ==[作答注意事項]==
                      <br>1. 請務必按照題號作答，
                        <span class="text-danger">未依題號作答部分不予計分</span>。
                      <br>2. 電子試卷作答區計
                        <span class="text-danger"> {{ CurrentQuestionLimitPage }} 頁</span>
                        ，請自行斟酌使用,不得跨題作答。
                      <br>3. 電子試卷上不可登打姓名、座號或其他不應有之文字、標記。
                      <br>4. 本題為申論題,請使用鍵盤及滑鼠輸入作答內容,由左至右橫式作答。
                    </p>
                  </div>
                  <el-button>作答注意事項</el-button>
                </el-tooltip>
                </div>
                <!-- 考選部拔除此功能，先關閉 start -->
                <div class="marker-selector" v-if="false">
                  <label class="marker-label">輔助作答註記：</label>
                  <div class="marker-radio-group">
                    <label
                      v-for="marker in MarkerTypes"
                      :key="marker.id"
                      class="marker-radio"
                    >
                      <input
                        type="radio"
                        :name="`marker-${CurrentQuestion.essayQuestionsId}`"
                        :value="marker.id"
                        v-model="MarkerSelections[CurrentQuestion.essayQuestionsId]"
                      />
                      <span class="marker-content">
                        <i
                          class="fa"
                          :class="[GetMarkerIcon(marker.id), GetMarkerClass(marker.id)]"
                        ></i>
                        <span class="marker-text">{{ marker.name }}</span>
                      </span>
                    </label>
                  </div>
                </div>
                <!-- 考選部拔除此功能，先關閉 end -->
              </div>

              <div class="toolbar-center">
                <div class="question-selector-wrapper">
                  <span class="current-question-label">目前在</span>
                  <select
                    :value="CurrentQuestionIndex"
                    @change="OnQuestionSelectChange($event)"
                    class="form-control form-control-sm question-select"
                  >
                    <option
                      v-for="(q, idx) in Questions"
                      :key="q.essayQuestionsId"
                      :value="idx"
                    >
                      第 {{ idx + 1 }} 題
                    </option>
                  </select>
                </div>
              </div>

              <div class="toolbar-right">
                <span class="page-indicator">
                  第 {{ CurrentQuestionIndex + 1 }} / {{ Questions.length }} 題
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Question Panel -->
        <div class="row mb-2" v-if="!IsSingleQuestionPreview">
          <div class="col-12">
            <div class="question-panel">
              <div class="panel-header" :class="{'hide': IsQuestionCollapsed}">
                <span class="question-title">題目 {{ CurrentQuestionIndex + 1 }}</span>
                <span class="question-points">
                  （{{ CurrentQuestion.essayQuestionScore || 0 }} 分）
                </span>
                <button
                  class="btn btn-sm btn-light ml-auto"
                  style="padding: 2px 8px; font-size: 13px;"
                  @click="IsQuestionCollapsed = !IsQuestionCollapsed"
                  :title="IsQuestionCollapsed ? '展開題目' : '收合題目'"
                >
                  <i
                    class="fa"
                    :class="IsQuestionCollapsed ? 'fa-chevron-down' : 'fa-chevron-up'"
                  ></i>
                </button>
              </div>
              <div
                class="panel-body"
                v-show="!IsQuestionCollapsed"
                v-html="CurrentQuestion.essayQuestionContent"
              ></div>
            </div>
          </div>
        </div>

        <!-- Editor / Preview -->
        <div class="row mb-2">
          <div class="col-12">
            <!-- 預覽模式：顯示分頁預覽 -->
            <div v-if="IsSingleQuestionPreview" class="preview-pages-container">
              <div class="preview-grid">
                <div
                  v-for="(page, index) in PagedContent"
                  :key="index"
                  class="preview-page"
                >
                  <div class="page-header">第 {{ index + 1 }} 頁</div>
                  <div class="page-content note-lines">
                    <div
                      class="page-content-wrapper"
                      :style="{ marginTop: `-${page.offset}px` }"
                      v-html="page.content"
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 編輯模式：顯示編輯器 -->
            <Ckeditor
              v-else-if="IsEditorLoaded"
              ref="ckeditorRef"
              :key="CurrentQuestion.essayQuestionsId"
              :initial-html="EditorContent"
              :initial-mode="'answer'"
              :max-word-count="CurrentQuestionWordLimit"
            />
          </div>
        </div>
      </div>
    </template>

    <!-- 交卷確認對話框 -->
    <el-dialog :visible.sync="FinishDialogVisible" width="500px">
      <div class="dialog-body">
        <p class="text-center mb-3">
          <i class="fa fa-exclamation-triangle text-warning" style="font-size: 48px"></i>
        </p>
        <h5 class="text-center mb-3">注意：結束作答視同交卷<br>是否結束本次應考?</h5>
        <div class="summary-box">
          <div class="summary-row">
            <span class="label">總題數：</span>
            <span class="value">{{ Questions.length }} 題</span>
          </div>
          <div class="summary-row">
            <span class="label">已作答：</span>
            <span class="value">{{ AnsweredCount }} 題</span>
          </div>
          <div class="summary-row">
            <span class="label text-danger">未作答：</span>
            <span class="value text-danger">{{ UnansweredCount }} 題</span>
          </div>
        </div>
        <p class="text-center text-muted mt-3" style="font-size: 13px">
          ⚠️ 交卷後將無法再修改答案
        </p>
      </div>
      <template #footer>
        <button @click="FinishDialogVisible = false" class="btn btn-secondary">取消</button>
        <button @click="ConfirmFinishExam" class="btn btn-danger ml-1" :disabled="IsSubmitting">
          {{ IsSubmitting ? '交卷中...' : '確定交卷' }}
        </button>
      </template>
    </el-dialog>

    <!-- 裝置警告對話框（不可關閉，需勾選才能確定） -->
    <el-dialog
      :visible.sync="DeviceWarningVisible"
      width="520px"
      custom-class="device-warning-dialog"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false"
      title="裝置相容性提醒"
    >
      <div class="device-warning-body">
        <p class="device-warning-text">
          本測驗模擬真實考試環境，<strong>僅支援電腦版 Google Chrome 瀏覽器</strong>。
        </p>
        <p class="device-warning-text">
          偵測到您目前使用<strong>行動裝置或平板</strong>，為了確保操作順暢及練習成效，請更換至電腦設備進行測驗。
        </p>
        <div class="device-warning-check">
          <el-checkbox v-model="DeviceWarningChecked">
            我已知曉其他裝置可能發生顯示異常，且與真實考試環境不符，若造成數據遺失需自行承擔。
          </el-checkbox>
        </div>
      </div>
      <template #footer>
        <button
          class="btn btn-primary"
          :disabled="!DeviceWarningChecked"
          @click="DeviceWarningVisible = false"
        >
          確定，繼續進入
        </button>
      </template>
    </el-dialog>
  </section>
</template>

<script>
import Ckeditor from '@/components/Ckeditor4.vue';
import ExamInstructions from '@/components/EssayPapers/ExamInstructions.vue';
import {
  GetStudentEssayPaperDetail,
  SaveStudentEssayAnswer,
  SubmitStudentEssayPaper,
  GetEssayMarkerType,
} from '@/api/EssayPapersApi';

export default {
  name: 'EssayExam',
  components: {
    Ckeditor,
    ExamInstructions,
  },
  data() {
    return {
      CourseId: null,
      PaperId: null,
      PaperDetail: {},
      Questions: [],
      CurrentQuestionIndex: 0,
      Answers: {},
      EditorContent: '',
      ShowInstructions: true,
      IsPreviewMode: false,
      DurationTimer: null,
      CountdownTimer: null,
      TotalDurationSeconds: 0,
      CurrentDurationSeconds: 0,
      RemainingSeconds: 0,
      IsSubmitting: false,
      IsSaving: false,
      IsEditorLoaded: false,
      FinishDialogVisible: false,
      MarkerTypes: [],
      MarkerSelections: {},
      // 單題預覽模式開關
      IsSingleQuestionPreview: false,
      // 題目面板縮合
      IsQuestionCollapsed: false,
      // 分頁後的內容陣列
      PagedContent: [],
      // 裝置警告對話框
      DeviceWarningVisible: false,
      DeviceWarningChecked: false,
    };
  },
  computed: {
    CurrentQuestion() {
      return this.Questions[this.CurrentQuestionIndex] || {};
    },
    CurrentQuestionWordLimit() {
      return this.CurrentQuestion.essayQuestionWordLimit || 5200;
    },
    IsLastQuestion() {
      return this.CurrentQuestionIndex === this.Questions.length - 1;
    },
    IsFirstQuestion() {
      return this.CurrentQuestionIndex === 0;
    },
    AnsweredCount() {
      let count = 0;
      this.Questions.forEach((q) => {
        const answer = this.Answers[q.essayQuestionsId];
        if (answer && answer.trim() !== '') {
          count += 1;
        }
      });
      return count;
    },
    UnansweredCount() {
      return this.Questions.length - this.AnsweredCount;
    },
    TimeLimitSeconds() {
      return this.PaperDetail.allowedDurationSeconds || 0;
    },
    CurrentQuestionLimitPage() {
      return Math.ceil(this.CurrentQuestionWordLimit / (25 * 26));
    },
  },
  async created() {
    this.CourseId = this.$route.params.courseId;
    this.PaperId = this.$route.params.paperId;

    // 偵測行動裝置或平板
    // 注意：iPadOS 13+ 的 iPad Pro / iPad Air UA 偽裝成 Macintosh，
    // 需額外用 maxTouchPoints >= 1 來識別（Chrome DevTools 觸控模擬會設定 maxTouchPoints = 1，
    // 一般 Mac 桌機則為 0）
    const ua = navigator.userAgent;
    // UA 字串判斷（含 iPadOS 13+ 偽裝成 Macintosh 的情況）
    const uaIsMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)
      || (navigator.maxTouchPoints > 1 && /Macintosh/i.test(ua));
    // pointer: coarse = 觸控主輸入裝置，Chrome DevTools 裝置模擬會正確設定此值
    // 可涵蓋 iPad Air / iPad Pro 等所有尺寸，不受螢幕寬度或 UA 限制
    const hasCoarsePointer = window.matchMedia('(pointer: coarse)').matches;
    const isMobileOrTablet = uaIsMobile || hasCoarsePointer;
    if (isMobileOrTablet) {
      this.DeviceWarningVisible = true;
    }

    await this.LoadMarkerTypes();
    await this.LoadPaperDetail();
  },
  beforeDestroy() {
    this.ClearAllTimers();
  },
  methods: {
    async LoadMarkerTypes() {
      try {
        const res = await GetEssayMarkerType({ type: 0 });
        if (res.data.statusCode === 0) {
          this.MarkerTypes = res.data.data || [];
        }
      } catch (error) {
        // 載入標記類型失敗
      }
    },
    async LoadPaperDetail() {
      try {
        const res = await GetStudentEssayPaperDetail({
          essayPapersId: parseInt(this.PaperId, 10),
          courseId: parseInt(this.CourseId, 10),
        });

        if (res.data.statusCode === 0) {
          const { mainInfo, questions } = res.data.data;
          this.PaperDetail = mainInfo;
          this.Questions = questions || [];
          this.TotalDurationSeconds = mainInfo.totalDurationSeconds || 0;
          this.CurrentDurationSeconds = 0;

          this.Questions.forEach((q) => {
            if (q.answerContent) {
              this.Answers[q.essayQuestionsId] = q.answerContent;
            }
            // 初始化標記選擇，預設為題目的標記或無標記(0)
            this.MarkerSelections[q.essayQuestionsId] = q.markerType || 0;
          });

          if (mainInfo.status === 1) {
            this.ShowInstructions = false;
            this.LoadQuestion(0);
            this.StartExamTimers();
          }
        }
      } catch (error) {
        this.$message.error('載入試卷失敗');
      }
    },
    LoadQuestion(index) {
      this.CurrentQuestionIndex = index;
      const question = this.Questions[index];
      this.EditorContent = this.Answers[question.essayQuestionsId] || '';

      // 若目前在單題預覽模式，重新計算該題的分頁內容
      if (this.IsSingleQuestionPreview) {
        this.PagedContent = this.CalculatePages(this.EditorContent);
      }

      this.IsEditorLoaded = false;
      this.$nextTick(() => {
        this.IsEditorLoaded = true;
      });
    },
    StartExam() {
      this.ShowInstructions = false;
      this.LoadQuestion(0);
      this.StartExamTimers();
    },
    StartExamTimers() {
      this.DurationTimer = setInterval(() => {
        if (this.DeviceWarningVisible) return; // 若裝置警告對話框仍在，暫停計時
        this.TotalDurationSeconds += 1;
        this.CurrentDurationSeconds += 1;
        this.UpdateRemainingSeconds();

        // 每10秒觸發一次自動儲存
        if (this.CurrentDurationSeconds === 10) {
          this.SaveCurrentAnswer(true);
        }
      }, 1000);

      this.UpdateRemainingSeconds();
    },
    UpdateRemainingSeconds() {
      if (this.TimeLimitSeconds > 0) {
        this.RemainingSeconds = Math.max(
          0,
          this.TimeLimitSeconds - this.TotalDurationSeconds,
        );

        if (this.RemainingSeconds === 0 && !this.IsPreviewMode) {
          this.HandleTimeUp();
        }
      }
    },
    CancelExam() {
      this.$router.push({
        name: 'essayPapersList',
        params: {
          courseId: this.CourseId,
        },
      });
    },
    ClearAllTimers() {
      if (this.DurationTimer) clearInterval(this.DurationTimer);
      if (this.CountdownTimer) clearInterval(this.CountdownTimer);
    },
    async SaveCurrentAnswer(isAutoSave = false, isFinalSave = false) {
      // 如果正在儲存，等待完成
      if (this.IsSaving) {
        // 自動儲存時，如果已在儲存中就跳過
        if (isAutoSave) return;
        // 手動儲存時，等待當前儲存完成
        await new Promise((resolve) => {
          const checkInterval = setInterval(() => {
            if (!this.IsSaving) {
              clearInterval(checkInterval);
              resolve();
            }
          }, 50);
        });
      }

      this.IsSaving = true;

      // ⭐ 獲取不含頁碼標記的 HTML
      if (this.$refs.ckeditorRef) {
        // 方法 1: 優先使用 getCleanHTML() 方法
        if (typeof this.$refs.ckeditorRef.getCleanHTML === 'function') {
          this.EditorContent = this.$refs.ckeditorRef.getCleanHTML();
        } else if (this.$refs.ckeditorRef.editorData) {
          // 方法 2: 使用組件的 editorData（已在 change 事件中清理過）
          this.EditorContent = this.$refs.ckeditorRef.editorData;
        } else if (this.$refs.ckeditorRef.editorInstance) {
          // 方法 3: 備用方案 - 直接從編輯器實例獲取
          this.EditorContent = this.$refs.ckeditorRef.editorInstance.getData();
        }
      }

      const question = this.CurrentQuestion;

      // 立即獲取並重置時間，避免儲存過程中繼續累計導致時間不準確
      const durationToSave = this.CurrentDurationSeconds;
      this.CurrentDurationSeconds = 0;

      try {
        const markerType = this.MarkerSelections[question.essayQuestionsId] || 0;
        await SaveStudentEssayAnswer({
          essayPapersId: parseInt(this.PaperId, 10),
          courseId: parseInt(this.CourseId, 10),
          essayQuestionsId: question.essayQuestionsId,
          answerContent: this.EditorContent,
          markerType,
          currentDurationSeconds: durationToSave,
        }, !isAutoSave);

        this.Answers[question.essayQuestionsId] = this.EditorContent;
        this.Answers = { ...this.Answers };

        // 手動儲存顯示提示訊息
        if (!isAutoSave && !isFinalSave) {
          this.$message.success('已儲存');
        }
      } catch (error) {
        // 儲存失敗時，將時間加回去避免遺失
        this.CurrentDurationSeconds += durationToSave;
        if (!isAutoSave) {
          this.$message.error('儲存失敗');
        }
      } finally {
        this.IsSaving = false;
      }
    },
    async PrevQuestion() {
      await this.SaveCurrentAnswer();
      if (!this.IsFirstQuestion) {
        this.LoadQuestion(this.CurrentQuestionIndex - 1);
      }
    },
    async NextQuestion() {
      await this.SaveCurrentAnswer();
      if (!this.IsLastQuestion) {
        this.LoadQuestion(this.CurrentQuestionIndex + 1);
      }
    },
    async OnQuestionSelectChange(event) {
      const newIndex = parseInt(event.target.value, 10);
      if (newIndex === this.CurrentQuestionIndex) return;

      await this.SaveCurrentAnswer();

      // 再切換到新題目
      this.LoadQuestion(newIndex);
    },
    async GoToPreview() {
      await this.SaveCurrentAnswer();
      this.IsPreviewMode = true;

      // 瀏覽模式暫停計時和自動儲存
      if (this.DurationTimer) clearInterval(this.DurationTimer);

      this.StartPreviewCountdown();
    },
    StartPreviewCountdown() {
      this.CountdownTimer = setInterval(() => {
        if (this.RemainingSeconds > 0) {
          this.RemainingSeconds -= 1;
        } else {
          this.HandleTimeUp();
        }
      }, 1000);
    },
    ContinueExam() {
      this.IsPreviewMode = false;

      if (this.CountdownTimer) clearInterval(this.CountdownTimer);

      // 重新啟動計時器繼續計時和自動儲存
      this.StartExamTimers();

      this.LoadQuestion(this.CurrentQuestionIndex);
    },
    GoToQuestionFromPreview(index) {
      this.CurrentQuestionIndex = index;
      this.ContinueExam();
    },
    ShowFinalConfirmDialog() {
      this.FinishDialogVisible = true;
    },
    ConfirmFinishExam() {
      this.$confirm('再次確定是否交卷？', '提示', {
        confirmButtonText: '確認',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(() => {
          this.FinishExam();
        })
        .catch(() => {});
    },
    async FinishExam() {
      this.IsSubmitting = true;
      try {
        await this.SaveCurrentAnswer(false, true);

        await SubmitStudentEssayPaper({
          essayPapersId: parseInt(this.PaperId, 10),
          courseId: parseInt(this.CourseId, 10),
        });

        this.$message.success('交卷成功！');
        this.ClearAllTimers();
        this.$router.push({ name: 'essayPapers' });
      } catch (error) {
        this.$message.error('交卷失敗');
      } finally {
        this.IsSubmitting = false;
        this.FinishDialogVisible = false;
      }
    },
    HandleTimeUp() {
      if (this.CountdownTimer) clearInterval(this.CountdownTimer);

      // this.$alert('考試時間已到，系統將自動交卷。', '時間到', {
      //   confirmButtonText: '確定',
      //   type: 'warning',
      //   showClose: false,
      //   closeOnClickModal: false,
      // }).then(() => {
      //   this.FinishExam();
      // });
    },
    PrepareHtmlForPreview(html) {
      if (!html) return html;
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const defaultTypes = ['disc', 'circle', 'square'];
      doc.querySelectorAll('ul').forEach((ul) => {
        const existingStyle = ul.getAttribute('style') || '';
        if (!/list-style-type\s*:/.test(existingStyle)) {
          let depth = 0;
          let ancestor = ul.parentElement;
          while (ancestor && ancestor !== doc.body) {
            if (ancestor.tagName === 'UL') depth += 1;
            ancestor = ancestor.parentElement;
          }
          const listType = defaultTypes[depth % defaultTypes.length];
          const newStyle = existingStyle
            ? `${existingStyle.replace(/;\s*$/, '')}; list-style-type: ${listType};`
            : `list-style-type: ${listType};`;
          ul.setAttribute('style', newStyle);
        }
      });
      return doc.body.innerHTML;
    },
    HasAnswer(questionId) {
      const answer = this.Answers[questionId];
      return answer && answer.trim() !== '';
    },
    GetAnswerPreview(questionId) {
      const html = this.Answers[questionId] || '';
      if (!html) return '';

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const defaultTypes = ['disc', 'circle', 'square'];

      // 逐一檢查每個 ul 的 style 屬性
      // 若已有明確的 list-style-type 則保留，否則依巢狀層級補上預設值
      doc.querySelectorAll('ul').forEach((ul) => {
        const existingStyle = ul.getAttribute('style') || '';
        if (!/list-style-type\s*:/.test(existingStyle)) {
          let depth = 0;
          let ancestor = ul.parentElement;
          while (ancestor && ancestor !== doc.body) {
            if (ancestor.tagName === 'UL') depth += 1;
            ancestor = ancestor.parentElement;
          }
          const listType = defaultTypes[depth % defaultTypes.length];
          const newStyle = existingStyle
            ? `${existingStyle.replace(/;\s*$/, '')}; list-style-type: ${listType};`
            : `list-style-type: ${listType};`;
          ul.setAttribute('style', newStyle);
        }
      });

      return doc.body.innerHTML;
    },
    FormatTime(seconds) {
      const h = Math.floor(seconds / 3600);
      const m = Math.floor((seconds % 3600) / 60);
      const s = seconds % 60;
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    },
    GetMarkerIcon(markerType) {
      const icons = {
        1: 'fa-exclamation-triangle',
        2: 'fa-square',
        3: 'fa-square',
        4: 'fa-ban',
      };
      return icons[markerType] || '';
    },
    GetMarkerClass(markerType) {
      const classes = {
        1: 'marker-yellow',
        2: 'marker-red',
        3: 'marker-blue',
        4: 'marker-none',
      };
      return classes[markerType] || '';
    },
    async TogglePreviewMode() {
      if (this.IsSingleQuestionPreview) {
        // 預覽 → 編輯
        this.IsSingleQuestionPreview = false;
        this.PagedContent = [];
      } else {
        // 編輯 → 預覽
        // 1. 先儲存當前答案
        await this.SaveCurrentAnswer(true);

        // 2. 從編輯器獲取最新的乾淨內容（不含頁碼標記）
        let content = '';
        if (this.$refs.ckeditorRef) {
          // 優先使用 getCleanHTML() 方法
          if (typeof this.$refs.ckeditorRef.getCleanHTML === 'function') {
            content = this.$refs.ckeditorRef.getCleanHTML();
          } else if (this.$refs.ckeditorRef.editorData) {
            // 使用組件的 editorData（已在 change 事件中清理過）
            content = this.$refs.ckeditorRef.editorData;
          } else if (this.$refs.ckeditorRef.editorInstance) {
            // 備用方案
            content = this.$refs.ckeditorRef.editorInstance.getData();
          } else {
            content = this.EditorContent || '';
          }
        } else {
          content = this.EditorContent || '';
        }

        // 3. 計算分頁
        this.PagedContent = this.CalculatePages(content);

        // 4. 切換顯示
        this.IsSingleQuestionPreview = true;
      }
    },
    CalculatePages(htmlContent) {
      if (!htmlContent || htmlContent.trim() === '') {
        return [{ content: '<p style="color: #999; text-align: center; padding-top: 200px;">（尚未作答）</p>', offset: 0 }];
      }

      // 注入 list-style-type 到 ul 的 inline style，使 CSS inherit 能正確繼承
      const processedHtml = this.PrepareHtmlForPreview(htmlContent);

      const pages = [];
      const tempDiv = document.createElement('div');

      // 注入與 .page-content 完全相同的列表 CSS，確保量測高度與預覽渲染一致
      const measureStyles = document.createElement('style');
      measureStyles.textContent = `
        /* 強制所有子元素使用與 .page-content 完全相同的字型規格，避免繼承不確定性 */
        .measure-container,
        .measure-container * {
          font-family: "標楷體", "DFKai-SB", "BiauKai", "STKaiti", serif !important;
          font-size: 18px !important;
          letter-spacing: 3px !important;
          line-height: 36px !important;
        }
        .measure-container *::before,
        .measure-container *::after {
          font-family: "標楷體", "DFKai-SB", "BiauKai", "STKaiti", serif !important;
          font-size: 18px !important;
          letter-spacing: 3px !important;
          line-height: 36px !important;
        }
        .measure-container ol {
          list-style: none !important;
          counter-reset: item;
          padding-left: 0 !important;
        }
        .measure-container ol > li {
          --mk: 1.5em;
          counter-increment: item;
          display: table;
          width: 100%;
          border-spacing: 0;
          line-height: 36px !important;
          margin: 0;
          word-break: break-all;
        }
        .measure-container ol > li::before {
          content: counter(item) '.';
          display: table-cell;
          width: 1%;
          white-space: nowrap;
          vertical-align: top;
          line-height: 36px !important;
        }
        .measure-container ol > li > ol,
        .measure-container ol > li > ul {
          display: table-caption;
          caption-side: bottom;
          padding-left: var(--mk) !important;
          word-break: break-all;
        }
        .measure-container ol.parentheses-list > li::before {
          content: '(' counter(item) ')';
          display: table-cell;
          width: 1%;
          white-space: nowrap;
          vertical-align: top;
          line-height: 36px !important;
        }
        .measure-container ol.parentheses-list > li {
          --mk: 2em;
          display: table;
          width: 100%;
          border-spacing: 0;
          line-height: 36px !important;
          margin: 0;
          word-break: break-all;
        }
        .measure-container ol.parentheses-list > li > ol,
        .measure-container ol.parentheses-list > li > ul {
          display: table-caption;
          caption-side: bottom;
          padding-left: var(--mk) !important;
          word-break: break-all;
        }
        .measure-container ol.parentheses-chinese-list > li::before {
          content: '(' counter(item, cjk-ideographic) ')';
          display: table-cell;
          width: 1%;
          white-space: nowrap;
          vertical-align: top;
          line-height: 36px !important;
        }
        .measure-container ol.parentheses-chinese-list > li {
          --mk: 2em;
          display: table;
          width: 100%;
          border-spacing: 0;
          line-height: 36px !important;
          margin: 0;
          word-break: break-all;
        }
        .measure-container ol.parentheses-chinese-list > li > ol,
        .measure-container ol.parentheses-chinese-list > li > ul {
          display: table-caption;
          caption-side: bottom;
          padding-left: var(--mk) !important;
          word-break: break-all;
        }
        .measure-container ol.cjk-ideographic-list > li::before {
          content: counter(item, cjk-ideographic) '、';
          display: table-cell;
          width: 1%;
          white-space: nowrap;
          vertical-align: top;
          line-height: 36px !important;
        }
        .measure-container ol.cjk-ideographic-list > li {
          --mk: 1.5em;
          display: table;
          width: 100%;
          border-spacing: 0;
          line-height: 36px !important;
          margin: 0;
          word-break: break-all;
        }
        .measure-container ol.cjk-ideographic-list > li > ol,
        .measure-container ol.cjk-ideographic-list > li > ul {
          display: table-caption;
          caption-side: bottom;
          padding-left: var(--mk) !important;
          word-break: break-all;
        }
        .measure-container ul {
          padding-left: 1.5em !important;
        }
        .measure-container ul > li {
          line-height: 36px !important;
          list-style: inherit !important;
          padding-left: 1em !important;
          margin: 0;
          word-break: break-all;
        }
        .measure-container ul > li > ol,
        .measure-container ul > li > ul {
          padding-left: 1em !important;
        }
        .measure-container p {
          margin: 0;
          padding: 0;
          line-height: 36px !important;
          word-break: break-all;
        }
        .measure-container ol,
        .measure-container li,
        .measure-container ul {
          margin-top: 0 !important;
          margin-bottom: 0 !important;
          padding-top: 0 !important;
          padding-bottom: 0 !important;
        }
      `;

      tempDiv.className = 'measure-container';
      tempDiv.innerHTML = processedHtml;
      tempDiv.style.cssText = `
        box-sizing: border-box;
        font-family: "標楷體", "DFKai-SB", "BiauKai", "STKaiti", serif;
        font-size: 18px;
        line-height: 36px;
        letter-spacing: 3px;
        width: 534px;
        max-width: 534px;
        position: absolute;
        left: -9999px;
        top: -9999px;
        visibility: hidden;
        overflow: visible;
        padding: 2px;
      `;
      document.head.appendChild(measureStyles);
      document.body.appendChild(tempDiv);

      // 每頁高度（936px = 26行 × 36px）
      const PAGE_HEIGHT = 936;

      try {
        // 扣除 padding（上下各 2px = 4px），取得純內容高度
        const totalHeight = tempDiv.scrollHeight - 4;
        // 使用 epsilon (0.5) 避免 sub-pixel 浮點誤差導致多算一頁
        const totalPages = Math.min(Math.ceil((totalHeight - 0.5) / PAGE_HEIGHT) || 1, 20);

        // 每頁都使用完整內容，但用不同的偏移量來顯示不同部分
        for (let i = 0; i < totalPages; i += 1) {
          pages.push({
            content: processedHtml,
            offset: i * PAGE_HEIGHT,
          });
        }
      } catch (error) {
        pages.push({ content: processedHtml, offset: 0 });
      } finally {
        document.body.removeChild(tempDiv);
        document.head.removeChild(measureStyles);
      }

      return pages.length > 0 ? pages : [{ content: processedHtml, offset: 0 }];
    },
  },
};
</script>

<style lang="scss" scoped>
/* ========== Header ========== */
.exam-header {
  padding: 10px 20px;
  border-radius: 6px 6px 0 0;
  border: 1px solid #e1e4e8;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.bg-green {
  background: #f0fdf4;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.exam-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #212529;
}

.header-right {
  display: flex;
  gap: 8px;
}

/* ========== Status Bar ========== */
.status-bar {
  background: #fff;
  padding: 12px 20px;
  border-radius: 0 0 6px 6px;
  border: 1px solid #e1e4e8;
  border-top: none;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.status-row {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  align-items: center;
  padding: 6px 0;
}

.status-divider {
  width: 100%;
  height: 1px;
  background: #f3f4f4;
  margin: 4px 0;
}

.status-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
}

.status-item i {
  color: #586069;
}

.status-icon-red {
  color: #dc3545 !important;
}

.label {
  color: #586069;
}

.value {
  font-weight: 600;
  color: #212529;
}

.text-red {
  color: #dc3545 !important;
}

.status-item.warning {
  background: #fff3cd;
  padding: 6px 12px;
  border-radius: 4px;
  border: 1px solid #ffc107;
}

.status-item.warning i,
.status-item.warning .label,
.status-item.warning .value {
  color: #856404;
}

/* ========== Toolbar ========== */
.exam-toolbar {
  background: #f3f4f6;
  padding: 10px 16px;
  border-radius: 6px;
  border: 1px solid #e1e4e8;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.toolbar-left,
.toolbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.toolbar-center {
  flex: 1;
  display: flex;
  justify-content: center;
}

.question-selector-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.current-question-label {
  font-size: 14px;
  color: #586069;
  font-weight: 500;
  white-space: nowrap;
}

.question-select {
  min-width: 120px;
  font-size: 14px;
  cursor: pointer;
}

.page-indicator {
  font-size: 13px;
  color: #586069;
  font-weight: 500;
}

/* ========== Question Panel ========== */
.question-panel {
  background: #fff;
  border: 1px solid #e1e4e8;
  border-radius: 6px;
  padding: 10px 20px;
}

.panel-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e1e4e8;
  margin-bottom: 16px;
}
.panel-header.hide {
  padding-bottom: 0;
  margin-bottom: 0;
  border-bottom: none;
}

.question-title {
  font-size: 16px;
  font-weight: 600;
  color: #0096a4;
}

.question-points {
  font-size: 14px;
  color: #586069;
}

.panel-body {
  font-size: 15px;
  line-height: 1.8;
  color: #212529;
}

/* ========== Notice Tip ========== */
.notice-tip {
  background: #f8f9fa;
  border: 1px solid #e1e4e8;
  border-radius: 4px;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #6c757d;
}

.notice-tip i {
  font-size: 13px;
  flex-shrink: 0;
  color: #6c757d;
}

/* ========== Word Count ========== */
.text-success {
  color: #22c55e;
}

.text-warning {
  color: #fbbf24;
}

.text-danger {
  color: #dc3545;
}

/* ========== Review Table ========== */
.review-table {
  background: #fff;
  border-radius: 6px;
  border: 1px solid #e1e4e8;
  overflow: hidden;
}

.table-header {
  display: flex;
  background: #fff9c4;
  padding: 14px 20px;
  font-weight: 600;
  border-bottom: 2px solid #e1e4e8;
}

.col-number {
  width: 120px;
  flex-shrink: 0;
}

.col-content {
  flex: 1;
}

.table-body {
  max-height: 600px;
  overflow-y: auto;
}

.table-row {
  display: flex;
  padding: 16px 20px;
  border-bottom: 1px solid #e1e4e8;
  background: #f8f9fa;
  transition: background 0.2s;
}

.table-row:hover {
  background: #e9ecef;
}

.table-row.unanswered {
  background: #fee2e2;
}

.table-row.unanswered:hover {
  background: #fecaca;
}

.question-link {
  color: #0366d6;
  text-decoration: none;
  font-weight: 500;
}

.question-link:hover {
  text-decoration: underline;
}

::v-deep .answer-preview {
  font-size: 14px;
  line-height: 1.6;
  color: #24292e;

  /* ========== 標準數字列表 ========== */
  ol {
    list-style: none !important;
    counter-reset: item;
    padding-left: 0 !important;
  }

  ol > li {
    --mk: 1.5em;
    counter-increment: item;
    display: table;
    width: 100%;
    border-spacing: 0;
    line-height: 1.8;
    margin-bottom: 0.5em;
    word-break: break-all;
  }

  ol > li::before {
    content: counter(item) '.';
    display: table-cell;
    width: 1%;
    white-space: nowrap;
    vertical-align: top;
    line-height: 1.8;
  }

  /* 子層列表處理 */
  ol > li > ol,
  ol > li > ul {
    display: table-caption;
    caption-side: bottom;
    margin-top: 0.5em;
    padding-left: var(--mk) !important;
    word-break: break-all;
  }

  /* ========== 括號數字列表 (1), (2), (3) ========== */
  ol.parentheses-list {
    list-style: none !important;
    counter-reset: item;
    padding-left: 0 !important;
  }

  ol.parentheses-list > li {
    --mk: 2em;
    counter-increment: item;
    display: table;
    width: 100%;
    border-spacing: 0;
    line-height: 1.8;
    margin-bottom: 0.5em;
    word-break: break-all;
  }

  ol.parentheses-list > li::before {
    content: '(' counter(item) ')';
    display: table-cell;
    width: 1%;
    white-space: nowrap;
    vertical-align: top;
    line-height: 1.8;
  }

  ol.parentheses-list > li > ol,
  ol.parentheses-list > li > ul {
    display: table-caption;
    caption-side: bottom;
    margin-top: 0.5em;
    padding-left: var(--mk) !important;
    word-break: break-all;
  }

  /* ========== 括號中文數字列表 (一)、(二)、(三) ========== */
  ol.parentheses-chinese-list {
    list-style: none !important;
    counter-reset: item;
    padding-left: 0 !important;
  }

  ol.parentheses-chinese-list > li {
    --mk: 2em;
    counter-increment: item;
    display: table;
    width: 100%;
    border-spacing: 0;
    line-height: 1.8;
    margin-bottom: 0.5em;
    word-break: break-all;
  }

  ol.parentheses-chinese-list > li::before {
    content: '(' counter(item, cjk-ideographic) ')';
    display: table-cell;
    width: 1%;
    white-space: nowrap;
    vertical-align: top;
    line-height: 1.8;
  }

  ol.parentheses-chinese-list > li > ol,
  ol.parentheses-chinese-list > li > ul {
    display: table-caption;
    caption-side: bottom;
    margin-top: 0.5em;
    padding-left: var(--mk) !important;
    word-break: break-all;
  }

  /* ========== 中文數字列表 一、二、三 ========== */
  ol.cjk-ideographic-list {
    list-style: none !important;
    counter-reset: item;
    padding-left: 0 !important;
  }

  ol.cjk-ideographic-list > li {
    --mk: 1.5em;
    counter-increment: item;
    display: table;
    width: 100%;
    border-spacing: 0;
    line-height: 1.8;
    margin-bottom: 0.5em;
    word-break: break-all;
  }

  ol.cjk-ideographic-list > li::before {
    content: counter(item, cjk-ideographic) '、';
    display: table-cell;
    width: 1%;
    white-space: nowrap;
    vertical-align: top;
    line-height: 1.8;
  }

  ol.cjk-ideographic-list > li > ol,
  ol.cjk-ideographic-list > li > ul {
    display: table-caption;
    caption-side: bottom;
    margin-top: 0.5em;
    padding-left: var(--mk) !important;
    word-break: break-all;
  }

  /* ========== 無序列表 ========== */
  /* list-style-type 由 GetAnswerPreview() 動態寫入每個 ul 的 style 屬性 */
  /* CSS 只需確保 li 繼承父 ul 的值，覆蓋全域 _base.scss 的 li { list-style: none } */
  ul {
    padding-left: 1.5em !important;
  }

  ul > li {
    list-style-type: inherit;
    line-height: 1.8;
    margin-bottom: 0.5em;
    padding-left: 1em !important;
    word-break: break-all;
  }

  ul > li > ol,
  ul > li > ul {
    margin-top: 0.5em;
    padding-left: 1em !important;
  }
}

.table-row:hover .answer-preview::after {
  background: linear-gradient(transparent, #e9ecef);
}

.table-row.unanswered .answer-preview::after {
  background: linear-gradient(transparent, #fee2e2);
}

.table-row.unanswered:hover .answer-preview::after {
  background: linear-gradient(transparent, #fecaca);
}

.empty-text {
  color: #dc3545;
  font-style: italic;
}

.footer-note {
  margin-top: 12px;
  padding: 10px 16px;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 4px;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
}

/* ========== Dialog ========== */
.dialog-body {
  padding: 20px 0;
}

.summary-box {
  background: #f8f9fa;
  border-radius: 6px;
  padding: 16px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #e1e4e8;
}

.summary-row:last-child {
  border-bottom: none;
}

.summary-row .label {
  color: #586069;
  font-weight: 500;
}

.summary-row .value {
  font-weight: 600;
  color: #212529;
}

/* ========== Marker Selector ========== */
.marker-selector {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 6px;
  padding-left: 12px;
  border-left: 1px solid #d1d5da;
}

.marker-label {
  font-size: 13px;
  color: #586069;
  margin: 0;
  white-space: nowrap;
}

.marker-radio-group {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.marker-radio {
  display: flex;
  align-items: center;
  padding: 6px 12px;
  background: #fff;
  border: 1px solid #d1d5da;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  margin: 0;
}

.marker-radio:hover {
  background: #f6f8fa;
  border-color: #0366d6;
}

.marker-radio input[type="radio"] {
  display: none;
}

.marker-radio input[type="radio"]:checked + .marker-content {
  font-weight: 600;
}

.marker-radio:has(input:checked) {
  background: #e6f3ff;
  border-color: #0366d6;
  box-shadow: 0 0 0 2px rgba(3, 102, 214, 0.1);
}

.marker-content {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #24292e;
}

.marker-content i {
  font-size: 14px;
}

.marker-none {
  color: #6c757d;
}

.marker-text {
  white-space: nowrap;
}

/* ========== Marker Icons ========== */
.marker-icon {
  font-size: 10px;
  position: absolute;
  top: 2px;
  right: 2px;
}

.marker-yellow {
  color: #ffc107;
}

.marker-red {
  color: #dc3545;
}

.marker-blue {
  color: #0d6efd;
}

.col-number .marker-icon {
  position: static;
  margin-left: 8px;
  font-size: 14px;
}

/* ========== Preview Pages Container ========== */
.preview-pages-container {
  background: #fff;
  padding: 20px;
  border: 1px solid #e1e4e8;
  border-radius: 6px;
  min-height: 600px;
  overflow-x: auto;
}

.preview-grid {
  display: grid;
  grid-template-columns: repeat(2, 538px);
  gap: 20px;
  justify-content: center;
}

.preview-page {
  border: 2px solid #0573D7;
  border-radius: 4px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.page-header {
  border-bottom: 2px solid #0573D7;
  background: #fff;
  color: #0573D7;
  padding: 8px 12px;
  font-size: 13px;
  text-align: center;
}

.page-content {
  height: 936px;
  padding: 2px;
  font-family: "標楷體", "DFKai-SB", "BiauKai", "STKaiti", serif;
  font-size: 18px;
  line-height: 36px;
  letter-spacing: 3px;
  overflow: hidden;
  position: relative;

  /* 確保所有 ::v-deep 子元素使用相同字型規格（對應 measureStyles 的強制設定） */
  ::v-deep * {
    font-family: "標楷體", "DFKai-SB", "BiauKai", "STKaiti", serif !important;
    font-size: 18px !important;
    letter-spacing: 3px !important;
  }

  &.note-lines {
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='462.5' height='36'><line x1='0' y1='35' x2='462.5' y2='35' stroke='%230573d7' stroke-width='1'/></svg>");
    background-repeat: repeat-y;
    background-size: 100% 36px;
    background-position: center 0;
  }

  /* 繼承編輯器的列表樣式 */
  ::v-deep ol {
    list-style: none !important;
    counter-reset: item;
    padding-left: 0 !important;
  }

  ::v-deep ol > li {
    --mk: 1.5em;
    counter-increment: item;
    display: table;
    width: 100%;
    border-spacing: 0;
    line-height: 36px !important;
    margin: 0;
    word-break: break-all;
  }

  ::v-deep ol > li::before {
    content: counter(item) '.';
    display: table-cell;
    width: 1%;
    white-space: nowrap;
    vertical-align: top;
    line-height: 36px !important;
  }

  /* 子層列表處理 */
  ::v-deep ol > li > ol,
  ::v-deep ol > li > ul {
    display: table-caption;
    caption-side: bottom;
    padding-left: var(--mk) !important;
    word-break: break-all;
  }

  ::v-deep ol.parentheses-list > li::before {
    content: '(' counter(item) ')';
    display: table-cell;
    width: 1%;
    white-space: nowrap;
    vertical-align: top;
    line-height: 36px !important;
  }

  ::v-deep ol.parentheses-list > li {
    --mk: 2em;
    display: table;
    width: 100%;
    border-spacing: 0;
    line-height: 36px !important;
    margin: 0;
    word-break: break-all;
  }

  ::v-deep ol.parentheses-list > li > ol,
  ::v-deep ol.parentheses-list > li > ul {
    display: table-caption;
    caption-side: bottom;
    padding-left: var(--mk) !important;
    word-break: break-all;
  }

  ::v-deep ol.parentheses-chinese-list > li::before {
    content: '(' counter(item, cjk-ideographic) ')';
    display: table-cell;
    width: 1%;
    white-space: nowrap;
    vertical-align: top;
    line-height: 36px !important;
  }

  ::v-deep ol.parentheses-chinese-list > li {
    --mk: 2em;
    display: table;
    width: 100%;
    border-spacing: 0;
    line-height: 36px !important;
    margin: 0;
    word-break: break-all;
  }

  ::v-deep ol.parentheses-chinese-list > li > ol,
  ::v-deep ol.parentheses-chinese-list > li > ul {
    display: table-caption;
    caption-side: bottom;
    padding-left: var(--mk) !important;
    word-break: break-all;
  }

  ::v-deep ol.cjk-ideographic-list > li::before {
    content: counter(item, cjk-ideographic) '、';
    display: table-cell;
    width: 1%;
    white-space: nowrap;
    vertical-align: top;
    line-height: 36px !important;
  }

  ::v-deep ol.cjk-ideographic-list > li {
    --mk: 1.5em;
    display: table;
    width: 100%;
    border-spacing: 0;
    line-height: 36px !important;
    margin: 0;
    word-break: break-all;
  }

  ::v-deep ol.cjk-ideographic-list > li > ol,
  ::v-deep ol.cjk-ideographic-list > li > ul {
    display: table-caption;
    caption-side: bottom;
    padding-left: var(--mk) !important;
    word-break: break-all;
  }

  ::v-deep ul {
    padding-left: 1.5em !important;
  }

  ::v-deep ul > li {
    line-height: 36px !important;
    list-style: inherit !important;
    padding-left: 1em !important;
    margin: 0;
    word-break: break-all;
  }

  ::v-deep ul > li > ol,
  ::v-deep ul > li > ul {
    padding-left: 1em !important;
  }

  ::v-deep p {
    margin: 0;
    padding: 0;
    line-height: 36px !important;
    word-break: break-all;
  }

  /* 確保格線對齊 */
  &.note-lines ::v-deep p,
  &.note-lines ::v-deep ol,
  &.note-lines ::v-deep li,
  &.note-lines ::v-deep ul {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }
}

/* 分頁內容包裝層：不可有任何額外空間，確保偏移量計算與量測一致 */
.page-content-wrapper {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  line-height: 36px;
  word-break: break-all;
}

.btn-toggle-preview {
  &.active {
    background: #e6f3ff;
    border-color: #0366d6;
    color: #0366d6;
    font-weight: 600;
  }
}

/* ========== Responsive ========== */
@media (max-width: 1200px) {
  .preview-grid {
    grid-template-columns: 538px;
  }
}

@media (max-width: 768px) {
  .exam-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-left {
    width: 100%;
  }

  .header-right {
    width: 100%;
    justify-content: flex-end;
  }

  .exam-toolbar {
    flex-direction: column;
    align-items: flex-start;
  }

  .toolbar-left {
    width: 100%;
    flex-wrap: wrap;
    gap: 6px;
  }

  .toolbar-center,
  .toolbar-right {
    width: 100%;
    justify-content: center;
  }

  .marker-selector {
    border-left: none;
    margin-left: 0;
    padding-left: 0;
    margin-top: 4px;
    flex-wrap: wrap;
    gap: 6px;
    width: 100%;
  }

  .marker-radio-group {
    width: 100%;
  }

  .marker-radio {
    flex: 1;
    min-width: 0;
    justify-content: center;
  }

  .preview-pages-container {
    padding: 12px;
  }

  .preview-grid {
    grid-template-columns: 538px;
    justify-content: start;
  }

  .table-header,
  .table-row {
    flex-direction: column;
    gap: 8px;
  }

  .col-number {
    width: 100%;
  }
}

/* ========== Device Warning Dialog ========== */
.device-warning-body {
  text-align: center;
  padding: 8px 4px;
}

.device-warning-text {
  font-size: 15px;
  line-height: 1.7;
  color: #212529;
  margin-bottom: 10px;
  word-break: break-word;
}

.device-warning-check {
  margin-top: 20px;
  padding: 14px 16px;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 6px;
  text-align: left;
  font-size: 14px;
  line-height: 1.6;

  // 修正 el-checkbox label 在手機上不換行的問題
  ::v-deep .el-checkbox {
    display: flex;
    align-items: flex-start;
    white-space: normal;
  }

  ::v-deep .el-checkbox__label {
    white-space: normal;
    line-height: 1.6;
    word-break: break-word;
  }
}

@media (max-width: 576px) {
  ::v-deep .device-warning-dialog {
    width: 92% !important;
    margin: 0 auto;
  }

  .device-warning-body {
    padding: 4px 0;
  }

  .device-warning-text {
    font-size: 14px;
  }

  .device-warning-check {
    padding: 12px;
    font-size: 13px;
  }
}
</style>
